import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Data } from '../../../core/services/data';
import { AdminService } from '../../../core/services/admin.service';
import { NewsItem } from '../../../core/models/news';

@Component({
  selector: 'app-news-manager',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './news-manager.html',
  styleUrl: './news-manager.css'
})
export class NewsManager implements OnInit {
  private dataService = inject(Data);
  private adminService = inject(AdminService);

  newsList = signal<NewsItem[]>([]);
  isLoading = signal<boolean>(false);
  errorMessage = signal<string>('');
  successMessage = signal<string>('');

  // Form fields
  editingIndex = signal<number | null>(null);
  formHeadline = '';
  formTextContent = '';
  formHtmlTextContent = '';

  // Link popup
  showLinkPopup = signal<boolean>(false);
  linkText = '';
  linkType: 'ausschreibungen' | 'ergebnisse' | 'external' = 'ausschreibungen';
  externalUrl = '';

  // Delete confirmation
  showDeleteConfirm = signal<boolean>(false);
  newsToDelete = signal<{ index: number; headline: string } | null>(null);

  ngOnInit() {
    this.loadNews();
  }

  loadNews() {
    this.isLoading.set(true);
    this.dataService.getNews().subscribe({
      next: (news) => {
        this.newsList.set(news);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.errorMessage.set('Fehler beim Laden der Nachrichten');
        this.isLoading.set(false);
      }
    });
  }

  startEdit(index: number) {
    const news = this.newsList()[index];
    this.editingIndex.set(index);
    this.formHeadline = news.Headline;
    this.formTextContent = news.TextContent || '';
    this.formHtmlTextContent = news.HtmlTextContent;
  }

  cancelEdit() {
    this.editingIndex.set(null);
    this.resetForm();
  }

  resetForm() {
    this.formHeadline = '';
    this.formTextContent = '';
    this.formHtmlTextContent = '';
  }

  saveNews() {
    if (!this.formHeadline.trim()) {
      this.errorMessage.set('Überschrift ist erforderlich');
      return;
    }

    const newsData: Partial<NewsItem> = {
      Headline: this.formHeadline,
      TextContent: this.formTextContent || null,
      HtmlTextContent: this.formHtmlTextContent
    };

    const editIndex = this.editingIndex();

    if (editIndex !== null) {
      // Update existing news
      this.adminService.updateNews(editIndex, newsData).subscribe({
        next: (response) => {
          this.successMessage.set(response.message);
          this.loadNews();
          this.cancelEdit();
          setTimeout(() => this.successMessage.set(''), 3000);
        },
        error: (error) => {
          this.errorMessage.set(error.error?.message || 'Fehler beim Aktualisieren');
        }
      });
    } else {
      // Create new news
      this.adminService.createNews(newsData).subscribe({
        next: (response) => {
          this.successMessage.set(response.message);
          this.loadNews();
          this.resetForm();
          setTimeout(() => this.successMessage.set(''), 3000);
        },
        error: (error) => {
          this.errorMessage.set(error.error?.message || 'Fehler beim Erstellen');
        }
      });
    }
  }

  confirmDeleteNews(index: number) {
    const news = this.newsList()[index];
    this.newsToDelete.set({ index, headline: news.Headline });
    this.showDeleteConfirm.set(true);
  }

  cancelDelete() {
    this.showDeleteConfirm.set(false);
    this.newsToDelete.set(null);
  }

  deleteNews() {
    const news = this.newsToDelete();
    if (!news) return;

    this.adminService.deleteNews(news.index).subscribe({
      next: (response) => {
        this.successMessage.set(response.message);
        this.loadNews();
        setTimeout(() => this.successMessage.set(''), 3000);
        this.showDeleteConfirm.set(false);
        this.newsToDelete.set(null);
      },
      error: (error) => {
        this.errorMessage.set(error.error?.message || 'Fehler beim Löschen');
        this.showDeleteConfirm.set(false);
        this.newsToDelete.set(null);
      }
    });
  }

  openLinkPopup() {
    this.showLinkPopup.set(true);
    this.linkText = '';
    this.linkType = 'ausschreibungen';
    this.externalUrl = '';
  }

  closeLinkPopup() {
    this.showLinkPopup.set(false);
  }

  insertLink() {
    let linkHtml = '';
    let url = '';

    switch (this.linkType) {
      case 'ausschreibungen':
        url = 'https://sko-sued.de/Ausschreibungen';
        break;
      case 'ergebnisse':
        url = 'https://sko-sued.de/Ergebnisse';
        break;
      case 'external':
        url = this.externalUrl;
        break;
    }

    if (!this.linkText.trim()) {
      this.errorMessage.set('Bitte geben Sie einen Link-Text ein');
      return;
    }

    if (this.linkType === 'external' && !this.externalUrl.trim()) {
      this.errorMessage.set('Bitte geben Sie eine URL ein');
      return;
    }

    linkHtml = `<a target="_blank" class="text-blue-600 hover:text-blue-800 underline font-medium" href="${url}">${this.linkText}</a>`;

    // Insert the link at the end of the current HTML content
    this.formHtmlTextContent += linkHtml;

    this.closeLinkPopup();
  }
}
